import { Home, Heart, Calendar, ShoppingCart, Briefcase, MapPin, Bell, Users, Mail, Droplet, Zap, Wifi, Trash2, Phone, FileText, Palette, Ruler, FileCheck, HardHat, Download, Facebook } from 'lucide-react';

// Export the icon mapping for easy reference
export const iconMap = {
  Home,
  Heart,
  Calendar,
  ShoppingCart,
  Briefcase,
  MapPin,
  Bell,
  Users,
  Mail,
  Droplet,
  Zap,
  Wifi,
  Trash2,
  Phone,
  FileText,
  Palette,
  Ruler,
  FileCheck,
  HardHat,
  Download,
  Facebook
};

export const categories = [
  {
    name: 'Home Resources',
    icon: FileCheck,
    color: 'from-blue-500 to-cyan-500',
    iconBg: 'bg-blue-100',
    hoverBg: 'hover:bg-blue-50',
    activeBg: 'bg-blue-50',
    activeText: 'text-blue-600',
    subcategories: [
      {
        name: 'HOA ARC Forms',
        description: 'Architectural Review Committee forms and guidelines',
        links: [
          { 
            title: 'ARC Request Form', 
            url: process.env.PUBLIC_URL + '/forms/geneva_arc_request_form.pdf',
            services: 'Form for requesting approval for exterior modifications',
            isDownloadable: true,
            fileType: 'PDF',
            fileSize: '360 KB'
          },
          { 
            title: 'ARC Guidelines', 
            url: process.env.PUBLIC_URL + '/forms/geneva_arc_guidelines.pdf',
            services: 'Complete guidelines for exterior modifications',
            isDownloadable: true,
            fileType: 'PDF',
            fileSize: '1.9 MB'
          },
        ]
      },
      {
        name: 'Paint Information',
        description: 'Interior paint color',
        links: [
          { 
            title: 'Interior Color Palette (SW 7646 First Star)', 
            url: 'https://www.sherwin-williams.com/en-us/color/color-family/white-paint-colors/SW7646-first-star',
            services: 'Sherwin-Williams HOA color tool'
          }
        ]
      },
      {
        name: 'Filter Sizes & Maintenance',
        description: 'HVAC filter information and maintenance schedules',
        links: [
          { 
            title: 'HVAC Filter Size: 14x20', 
            url: 'https://amzn.to/4kBXbml',
            services: 'Amazon link to purchase 14x20 filters',
            isDownloadable: false
          },
          { 
            title: 'HVAC Filter Size: 20x25', 
            url: 'https://amzn.to/4kBXbml',
            services: 'Amazon link to purchase 20x25 filters',
            isDownloadable: false
          }
        ]
      }
    ]
  },
  {
    name: 'New Resident Info',
    icon: Home,
    color: 'from-pink-500 to-rose-500',
    iconBg: 'bg-rose-100',
    hoverBg: 'hover:bg-rose-50',
    activeBg: 'bg-rose-50',
    activeText: 'text-rose-600',
    subcategories: [
      {
        name: 'Utilities Setup',
        links: [
          { 
            title: 'Duke Energy Progress (Electricity)', 
            url: 'https://www.duke-energy.com',
            contact: '1-800-452-2777',
            services: 'Electricity service, outage reporting, new service connection'
          },
          { 
            title: 'Town of Fuquay-Varina Utilities (Water/Sewer)', 
            url: 'https://www.fuquay-varina.org/205/Utility-Customer-Service',
            contact: '(919) 552-1400',
            services: 'Water and sewer service, billing inquiries, service start/stop'
          },
          { 
            title: 'Dominion Energy NC (Natural Gas)', 
            url: 'https://www.dominionenergy.com/north-carolina-gas',
            contact: '1-877-776-2427',
            services: 'Natural gas service, gas leaks emergency call: 1-877-776-2427'
          },
          { 
            title: 'Spectrum (Internet/Cable)', 
            url: 'https://www.spectrum.com/moving',
            contact: '1-855-707-7328',
            services: 'Internet, TV, and phone services'
          },
          { 
            title: 'AT&T (Internet/Cable)', 
            url: 'https://www.att.com/moving/',
            contact: '1-800-288-2020',
            services: 'Internet, TV, and phone services'
          },
          { 
            title: 'Fuquay-Varina Waste Collection', 
            url: 'https://www.fuquay-varina.org/352/Garbage-Collection-Service',
            contact: '(919) 753-1027',
            services: 'Household garbage collection, recycling, yard waste'
          },
          { 
            title: 'Recycling Schedule Lookup', 
            url: 'https://www.fuquay-varina.org/DocumentCenter/View/655/Recycling-and-Loose-Leaf-Calendar-PDF',
            contact: '(919) 753-1027',
            services: 'Find your recycling schedule'
          },
          { 
            title: 'Bulk Item Pickup', 
            url: 'https://www.fuquay-varina.org/1739/Bulky-Item-Pickup-Form',
            contact: '(919) 753-1027',
            services: 'Schedule pickup for large items that don\'t fit in regular bins'
          }
        ]
      },
      {
        name: 'HOA and Lennar Services',
        links: [
          { 
            title: 'Lennar Customer Care', 
            url: 'https://www.lennar.com/contact/customercarewarranty',
            contact: '919-337-9444',
            services: 'New home warranty service, customer support'
          },
          { 
            title: 'HOA', 
            url: 'https://charleston.cincwebaxis.com/account/loginmodernthemes',
            services: 'HOA portal to pay the dues'
          },
        ]
      },
      {
        name: 'Mail & Package Delivery',
        links: [
          { 
            title: 'USPS Change of Address', 
            url: 'https://moversguide.usps.com/mgo/disclaimer',
            services: 'Official mail forwarding service'
          },
          { 
            title: 'Fuquay-Varina Post Office', 
            url: 'https://tools.usps.com/locations/details/1364279',
            contact: '(919) 552-2660',
            services: 'Mail services, PO boxes, shipping'
          }
        ]
      },
      {
        name: 'Schools & Education',
        links: [
          { 
            title: 'School Assignment Lookup', 
            url: 'https://www.wcpss.net/school-assignment',
            services: 'Find your assigned schools based on address'
          },
          { 
            title: 'South Lakes Elementary', 
            url: 'https://www.wcpss.net/southlakeses',
            contact: '(919) 694-8066',
            services: 'Grades K-5, school information and resources'
          },
          { 
            title: 'Fuquay-Varina Middle', 
            url: 'https://www.wcpss.net/fuquayvarinaes',
            contact: '(919) 557-2727',
            services: 'Grades 6-8, school information and resources'
          },
          { 
            title: 'Willow Springs High', 
            url: 'https://www.wcpss.net/willowspringhs',
            contact: '(919) 694-8030',
            services: 'Grades 9-12, school information and resources'
          },
          { 
            title: 'School Calendar', 
            url: 'https://www.wcpss.net/calendars',
            services: 'Academic year calendar, holidays, teacher workdays'
          }
        ]
      }
    ]
  },
  {
    name: 'Home Services',
    icon: Heart,
    color: 'from-purple-500 to-indigo-500',
    iconBg: 'bg-purple-100',
    hoverBg: 'hover:bg-purple-50',
    activeBg: 'bg-purple-50',
    activeText: 'text-purple-600',
    subcategories: [
      {
        name: 'Handymen & Contractors',
        contactInfo: [
          { 
            title: 'George\'s Handyman Services', 
            contact: '(919) 753-7960',
            services: 'General repairs, carpentry, drywall, painting, minor electrical and plumbing',
          }
        ]
      },
      {
        name: 'Electricians',
        contactInfo: [
          { 
            title: 'Rober\'s Electric', 
            contact: '(919) 888-1706',
            services: 'Electrical repairs, panel upgrades, lighting installation, ceiling fans, outlets',
          }
        ]
      },
      {
        name: 'Insurance',
        contactInfo: [
          { 
            title: 'Brightway Insurance', 
            contact: '(919) 893-4411',
            services: 'Home Insurance, Auto Insurance, Business Insurance, Life Insurance, and more', 
          }
        ]
      },
      {
        name: 'Home Inspection',
        contactInfo: [
          { 
            title: 'Trimark Home Inspection', 
            contact: '(919) 839-0102',
            services: 'Pre-purchase inspections, seller inspections, new construction inspections',
            url: 'https://www.thisnc.com/' 
          }
        ]
      },
      {
        name: 'House Cleaners',
        contactInfo: [
          { 
            title: 'Brandy Power Wash', 
            contact: '(919) 576-7102',
            services: 'Exterior power washing, pressure washing, and more', 
          },
          { 
            title: 'Xquisite Power Wash', 
            contact: '(919) 769-4030',
            services: 'Exterior power washing, pressure washing, and more', 
          },
        ]
      },
      {
        name: 'Chairs & Table Rentals',
        contactInfo: [
          { 
            title: 'Fuquay Community Chair Rentals', 
            contact: '(000) 000-0000',
            services: 'Tables, chairs rentals',
          },
        ]
      },
      {
        name: 'Plumbers',
        contactInfo: [
          { 
            title: 'Fuquay Plumbing', 
            contact: '(919) 796-1107',
            services: 'Drain cleaning, pipe repairs, water heater service, sump pumps, backflow prevention',
          }
        ]
      },
      {
        name: 'Photographers',
        contactInfo: [
          { 
            title: 'Fuquay Photography', 
            contact: '(919) 555-9012',
            services: 'Family portraits, newborn photography, maternity sessions, senior portraits',
            url: 'https://example.com/fuquay-photography' 
          }
        ]
      }
    ]
  },
  {
    name: 'Local Amenities',
    icon: MapPin,
    color: 'from-amber-500 to-orange-500',
    iconBg: 'bg-amber-100',
    hoverBg: 'hover:bg-amber-50',
    activeBg: 'bg-amber-50',
    activeText: 'text-amber-600',
    subcategories: [
      {
        name: 'Indian Grocery',
        description: 'Nearby Indian shopping options',
        contactInfo: '',
        links: [
          { title: 'Grocerind & Inis Kitchen', url: 'https://maps.app.goo.gl/nd7yDPST1CovMg5s5' },
          { title: 'Angadi - Indian Grocery Store', url: 'https://maps.app.goo.gl/WgYkUon7etAx2RPm6' }
        ]
      },
      {
        name: 'Shopping Centers',
        description: 'Nearby shopping options',
        contactInfo: '',
        links: [
          { title: 'Fuquay Crossing', url: 'https://www.google.com/maps/place/Fuquay+Crossing' },
          { title: 'Walmart Supercenter', url: 'https://www.walmart.com/store/2908-fuquay-varina-nc' },
          { title: 'Harris Teeter', url: 'https://www.harristeeter.com/store/324/fuquay-varina-nc' }
        ]
      },
      {
        name: 'Parks & Recreation',
        description: 'Parks and recreational facilities',
        contactInfo: 'Fuquay-Varina Parks & Recreation: (919) 552-1430',
        links: [
          { title: 'Geneva Community Pool', url: 'https://www.genevafuquay.com/amenities' },
          { title: 'South Park', url: 'https://www.fuquay-varina.org/Facilities/Facility/Details/South-Park-8' },
          { title: 'Fleming Loop Park', url: 'https://www.fuquay-varina.org/Facilities/Facility/Details/Fleming-Loop-Park-3' },
          { title: 'Fuquay-Varina Community Center', url: 'https://www.fuquay-varina.org/316/Community-Center' }
        ]
      },
      {
        name: 'Dining & Entertainment',
        description: 'Local dining and entertainment options',
        contactInfo: '',
        links: [
          { title: 'Fuquay-Varina Downtown', url: 'https://www.fuquay-varina.org/272/Downtown' },
          { title: 'Aviator Brewing Company', url: 'https://www.aviatorbrew.com/' },
          { title: 'Stars Theater & Arts Center', url: 'https://www.starsnc.org/' },
          { title: 'Fuquay-Varina Museums', url: 'https://fuquay-varina-museums.org/' }
        ]
      }
    ]
  }
];

export const events = [
  { title: 'Geneva Summer Outing', date: 'May 27, 2025', time: '10:00 AM', location: 'Jordan Lake State Park' },
];

export const quickLinks = [
  { 
    title: 'Pay HOA Dues', 
    icon: Home, 
    color: 'bg-blue-500',
    url: 'https://charleston.cincwebaxis.com/account/loginmodernthemes'
  },
  { title: 'Report Issue', icon: Bell, color: 'bg-red-500' },
  { title: 'Join Community Group', icon: Users, color: 'bg-green-500' },
  { 
    title: 'Facebook Group', 
    icon: Facebook, 
    color: 'bg-blue-600',
    url: 'https://www.alpha.facebook.com/share/g/19xkJWMvum/?mibextid=wwXIfr'
  }
];

export const communityStats = [
  { value: '350+', label: 'Homes', color: 'from-green-500 to-emerald-500' },
  { value: '15+', label: 'Floor Plans', color: 'from-blue-500 to-cyan-500' },
  { value: '5+', label: 'Community Amenities', color: 'from-purple-500 to-pink-500' }
]; 